import { Link } from "react-router-dom";
import { Trash2, ArrowUpRight, Loader2, AlertCircle, Unlock } from "lucide-react";
import { toolByKey } from "@/lib/tools";
import { fileUrl } from "@/lib/api";
import { useLang } from "@/i18n";

const Status = ({ status, unlocked }) => {
  const { t } = useLang();
  if (status === "processing") return <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-600"><Loader2 className="h-3 w-3 animate-spin" />{t("history.processing")}</span>;
  if (status === "failed") return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500"><AlertCircle className="h-3 w-3" />{t("history.failed")}</span>;
  if (unlocked) return <span data-testid="history-unlocked-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"><Unlock className="h-3 w-3" />{t("history.unlocked")}</span>;
  return <span className="text-xs font-medium text-emerald-600">{t("history.done")}</span>;
};

export const HistoryList = ({ jobs, onDelete }) => {
  const { t, locale, toolHref } = useLang();
  const fmtDate = (iso) => new Date(iso).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  if (!jobs.length)
    return <p data-testid="history-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">{t("history.empty")}</p>;
  return (
    <ul data-testid="history-list" className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
      {jobs.map((j) => {
        const tl = toolByKey(j.tool);
        return (
          <li key={j.job_id} data-testid={`history-item-${j.job_id}`} className="flex items-center gap-3 p-4 transition-colors hover:bg-slate-50 sm:gap-4">
            <div className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-200 ${j.tool === "remove_bg" ? "checkerboard" : "bg-slate-50"}`}>
              {j.status !== "failed" && <img src={fileUrl(j.job_id, "original")} alt="" loading="lazy" className="h-full w-full object-contain" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">{j.input_name}</p>
              <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: tl.color }} /><span className="truncate">{t(`tools.${tl.slug}.name`)} · {fmtDate(j.created_at)}</span>
              </p>
              <div className="mt-1.5 sm:hidden"><Status status={j.status} unlocked={j.unlocked} /></div>
            </div>
            <div className="hidden shrink-0 sm:block"><Status status={j.status} unlocked={j.unlocked} /></div>
            {j.status !== "failed" && (
              <Link to={`${toolHref(tl.slug)}?job=${j.job_id}`} data-testid={`history-open-${j.job_id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900">
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
            <button onClick={() => onDelete(j)} data-testid={`history-delete-${j.job_id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};
